import React, { useEffect } from "react";
import { useStateLocalStorage } from "@dotlegal/dotlegal-ui-components";
import { formatRightPaneLeft, formatSpacerWidth, formatStagePaneRight, railHandleDraggingAttribute, frameSlotAttribute } from "./Rail.styles";

// The expanded drawer (290) plus enough content to still be worth looking at (400). A rail dragged
// wider than this would leave the page behind it unusable, and the stage is the way to get the whole
// viewport.
const drawerAndMinimumContentWidth = 690;

export function getMaxRailWidth(viewportWidth: number, minWidth: number): number {
    return Math.max(minWidth, viewportWidth - drawerAndMinimumContentWidth);
}

export function clampRailWidth(width: number, viewportWidth: number, minWidth: number): number {
    return Math.min(Math.max(width, minWidth), getMaxRailWidth(viewportWidth, minWidth));
}

export interface RailWidthState {
    width: number;
    setWidth: (width: number) => void;
    minWidth: number;
}

/**
 * The width the rail has been dragged to, remembered across sessions and shared by both occupants: a
 * user who has made room for the chat expects a pane to open at the same width.
 */
export function useRailWidthState(storageKey: string, getDefaultWidth: (viewportWidth: number) => number, minWidth: number): RailWidthState {
    const [width, setWidth] = useStateLocalStorage(storageKey, getDefaultWidth(window.innerWidth));

    // Only ever narrows. A width the user has dragged to is their decision, so a wider viewport must not
    // push the rail back to the default; a narrower one has to, or the rail squeezes the page out. Runs
    // on mount too, because the stored width may have been dragged on a larger screen than this one.
    useEffect(() => {
        function clampToViewport() {
            const maxWidth = getMaxRailWidth(window.innerWidth, minWidth);
            if (width > maxWidth) {
                setWidth(maxWidth);
            }
        }

        clampToViewport();
        window.addEventListener("resize", clampToViewport);
        return () => window.removeEventListener("resize", clampToViewport);
    }, [width, setWidth, minWidth]);

    return { width, setWidth, minWidth };
}

function findRailFollowers(slot: string): Array<HTMLElement> {
    return Array.from(document.querySelectorAll<HTMLElement>(`[${frameSlotAttribute}="${slot}"]`));
}

/**
 * Drag-to-resize for the rail's left edge.
 *
 * React is not told about a drag until it ends. A profile (2026-09-17) put one width commit at
 * 700-2000 ms, nearly all of it re-rendering the page beside the rail, and even the press and the
 * release cost about a second each just by flipping a resizing flag in the context. So each move writes
 * three inline properties and nothing else: the right pane's left edge, the stage pane's right edge and
 * the spacer's width, through the same formatters the style props use, and a single setWidth on release
 * commits the final value to state and localStorage. The content beside the rail is a flex item and
 * follows the spacer through layout alone. The moves and the release are listened for on the window so
 * the drag always ends, even if the handle is re-created mid-drag; pointer capture on top keeps the
 * events coming when the cursor leaves the browser window.
 */
export function useRailResize(state: RailWidthState) {
    function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
        // Stops the drag from selecting the text it passes over.
        event.preventDefault();
        const handle = event.currentTarget;
        handle.setPointerCapture(event.pointerId);
        handle.setAttribute(railHandleDraggingAttribute, "");

        // Gathered once per drag: what follows the rail cannot change while the pointer is down, and a
        // query per move would be wasted work.
        const rightPanes = findRailFollowers("right");
        const stagePanes = findRailFollowers("stage");
        const spacers = findRailFollowers("spacer");
        const followers = [...rightPanes, ...stagePanes, ...spacers];
        // Easing an edge that changes every frame would leave it trailing the cursor.
        followers.forEach((follower) => (follower.style.transition = "none"));

        const start = { pointerX: event.clientX, width: state.width };
        let latestWidth = state.width;

        const onPointerMove = (moveEvent: PointerEvent) => {
            // Dragging left, towards the page, widens the rail, so the delta is the other way round.
            latestWidth = clampRailWidth(start.width + (start.pointerX - moveEvent.clientX), window.innerWidth, state.minWidth);
            // The rail is occupied for as long as there is a handle to drag, so a stage pane beside it
            // keeps its gap and the spacer keeps its room.
            rightPanes.forEach((pane) => (pane.style.left = formatRightPaneLeft(latestWidth)));
            stagePanes.forEach((pane) => (pane.style.right = formatStagePaneRight(latestWidth, true)));
            spacers.forEach((spacer) => (spacer.style.width = formatSpacerWidth(latestWidth, true)));
        };

        const endDrag = () => {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", endDrag);
            window.removeEventListener("pointercancel", endDrag);
            handle.removeAttribute(railHandleDraggingAttribute);
            followers.forEach((follower) => (follower.style.transition = ""));
            // The inline edges the drag left behind are deliberately kept: React's style props write these
            // very strings on commit, so nothing moves and nothing animates on release.
            state.setWidth(latestWidth);
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", endDrag);
        window.addEventListener("pointercancel", endDrag);
    }

    return { onPointerDown };
}
